import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import StepIndicator from './StepIndicator'; // Import shared component
import './Step2Component.css';

const MOBILE_BREAKPOINT = 640; // Define breakpoint

const Step2Component = ({ selectedStyle: initialSelectedStyle, onStyleSelect, onNext, onBack, currentStep, completedSteps }) => {
  const [selectedStyle, setSelectedStyle] = useState(initialSelectedStyle || null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const previewVideoRefs = useRef({});

  const artStyles = [
    { id: 'dreamy', name: 'Dreamy Style', videoSrc: "/videos/ghibli.mp4", available: true },
    { id: 'no_lineart', name: 'No Lineart', videoSrc: "/videos/animation.mp4", available: true }
    // Add more styles here if needed
  ];

  useEffect(() => {
      // Ensure parent state is updated if initial style changes
      if(initialSelectedStyle){
          setSelectedStyle(initialSelectedStyle);
      }
  }, [initialSelectedStyle]);

  // Effect to handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    // Call handler right away so state is correct initiall
    handleResize(); 

    // Cleanup listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures effect only runs on mount and unmount

  const handleSelect = (style) => {
    if (!style.available) return;
    setSelectedStyle(style);
    onStyleSelect(style); // Notify parent
  };

  // --- Video Hover Handlers (only used on desktop) --- 
  const handleMouseEnter = (styleId) => {
    // Guard: Don't run on mobile
    if (isMobile) return; 

    const video = previewVideoRefs.current[styleId];
    const style = artStyles.find(s => s.id === styleId);
    if (video && video.paused && style?.available) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {}); // Handle potential errors
      }
    }
  };

  const handleMouseLeave = (styleId) => {
    // Guard: Don't run on mobile
    if (isMobile) return; 

    const video = previewVideoRefs.current[styleId];
     const style = artStyles.find(s => s.id === styleId);
    if (video && !video.paused && style?.available) {
      video.pause();
      video.currentTime = 0; // Reset to start
    }
  };

  return (
    <div>
      {/* Wrap Indicator and Title */}
      <div className="card__header">
        <h2 className="card__title">Choose Animation Style</h2>
        <StepIndicator currentStep={currentStep} completedSteps={completedSteps} />
      </div>

      <div className="card__content content-area">
        <div className="style-grid">
          {artStyles.map((style) => (
            <div 
              key={style.id}
              className={`style-item ${!style.available ? 'unavailable' : ''} ${selectedStyle?.id === style.id ? 'selected' : ''}`}
              onClick={() => handleSelect(style)}
              // Conditionally add hover handlers only for non-mobile
              onMouseEnter={!isMobile ? () => handleMouseEnter(style.id) : undefined}
              onMouseLeave={!isMobile ? () => handleMouseLeave(style.id) : undefined}
            >
              <div className="style-video-wrapper">
                 {/* Conditionally render Video OR Image */}
                 {isMobile ? (
                    <img 
                      src={`${style.videoSrc.replace('.mp4', '-poster.jpg')}`}
                      alt={`${style.name} preview`}
                      className="style-poster-image"
                    />
                 ) : (
                    <video 
                      ref={(el) => { previewVideoRefs.current[style.id] = el; }}
                      src={style.videoSrc + "#t=0.1"} 
                      poster={`${style.videoSrc.replace('.mp4', '-poster.jpg')}`}
                      muted 
                      loop 
                      playsInline 
                      preload="metadata"
                      className="style-video"
                    ></video>
                 )}
                 {/* Selection Indicator */} 
                {selectedStyle?.id === style.id && style.available && (
                   <div className="selection-indicator">
                     <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                     </svg>
                   </div>
                 )}
                 {/* Coming Soon Badge */}
                 {!style.available && (
                   <div className="coming-soon-badge">Coming Soon</div>
                 )}
              </div>
              <span className="style-name">{style.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="nav-area">
        {/* Back Button */}
        <button className="card__button secondary back-button" onClick={onBack}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="button-arrow arrow-left">
              <path d="M7.828 11H20v2H7.828l5.364 5.364-1.414 1.414L4 12l7.778-7.778 1.414 1.414z"/>
          </svg>
          Back
        </button>

        {/* Next Button */} 
        <button 
            className={`card__button next-button ${!selectedStyle ? 'disabled' : ''}`}
            onClick={onNext} 
            disabled={!selectedStyle}
          > 
          Next
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="button-arrow">
              <path d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

Step2Component.propTypes = {
  selectedStyle: PropTypes.object, // Can be null initially
  onStyleSelect: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  completedSteps: PropTypes.number.isRequired,
};

export default Step2Component; 